import { useId, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalShell } from './ModalShell';
import { splitQuestion, useConfirmStore } from '@/shared/stores/confirm.store';

export interface ConfirmDialogProps {
  open: boolean;
  /** The question, naming the object: `Delete "Diwali offer"?` */
  title: string;
  /** What happens next, or what cannot be undone. */
  description?: ReactNode;
  /** Verb-first action label ("Delete", "Disconnect"). Defaults to common.confirm. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red action (default). False for a non-destructive confirmation. */
  destructive?: boolean;
  /** The action is running: both buttons disable and the action says so. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The one named confirmation (surfaces rule: destructive actions confirm in
 * the app, never with the browser's `window.confirm`). A centred ModalShell
 * card with the question as its title, an optional consequence line, Cancel
 * and the action. Cancel comes first, so it holds the initial focus and Enter
 * on an accidental open does not destroy anything.
 */
export function ConfirmDialog({
  open,
  title: rawTitle,
  description: rawDescription,
  confirmLabel,
  cancelLabel,
  destructive = true,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): ReactElement {
  const { t } = useTranslation();
  const id = useId();
  const split = rawDescription ? null : splitQuestion(rawTitle);
  const title = split ? split.title : rawTitle;
  const description = split ? split.rest : rawDescription;
  const titleId = `${id}-title`;
  const descId = `${id}-desc`;
  return (
    <ModalShell
      open={open}
      onClose={busy ? () => undefined : onCancel}
      labelledBy={titleId}
      describedBy={description ? descId : undefined}
      role="alertdialog"
      closeOnBackdropClick={!busy}
      zIndex={700}
    >
      <div className="card w-[min(440px,calc(100vw-32px))] shadow-pop" style={{ padding: 20 }} data-testid="confirm-dialog">
        <h2 id={titleId} className="font-display m-0 text-title text-[var(--ink)]" dir="auto">{title}</h2>
        {description ? (
          <p id={descId} className="mt-2 mb-0 text-body text-[var(--ink-2)]" dir="auto">{description}</p>
        ) : null}
        <div className="flex flex-wrap gap-2 mt-5 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel ?? t('common.cancel')}
          </button>
          <button
            type="button"
            className={destructive ? 'btn btn-destructive' : 'btn btn-primary'}
            onClick={onConfirm}
            disabled={busy}
            aria-busy={busy || undefined}
          >
            {confirmLabel ?? t('common.confirm')}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/**
 * Renders whatever `confirmDialog()` asked for. Mounted once, in main.tsx.
 */
export function ConfirmDialogHost(): ReactElement {
  const request = useConfirmStore((s) => s.request);
  const settle = useConfirmStore((s) => s.settle);
  return (
    <ConfirmDialog
      open={request !== null}
      title={request?.title ?? ''}
      description={request?.description}
      confirmLabel={request?.confirmLabel}
      cancelLabel={request?.cancelLabel}
      destructive={request?.destructive ?? true}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );
}
